import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Snackbar,
  Alert,
  TextField,
  Button,
  Stack,
  Switch,
} from "@mui/material";
import EmissionAndSequenceSettings from "./ElectronicInvoice/EmissionAndSequenceSettings";
import ElectronicSignatureSettings from "./ElectronicInvoice/ElectronicSignatureSettings";

const tdLabelSx = {
  width: 380,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 700,
  verticalAlign: "top",
};

const tdFieldSx = { p: 1.5, verticalAlign: "middle" };
const noteSx = { color: "#666", fontSize: 12, lineHeight: 1.25 };

function getCSRF() {
  const name = "csrftoken=";
  const parts = decodeURIComponent(document.cookie || "").split(";");
  for (const p of parts) {
    const t = p.trim();
    if (t.indexOf(name) === 0) return t.substring(name.length);
  }
  return "";
}

function getAuthHeaders(extra = {}) {
  const token = localStorage.getItem("access") || "";
  const headers = { ...extra };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export default function ElectronicInvoice() {
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [firmaEstado, setFirmaEstado] = useState({});
  const [firmadoAutomatico, setFirmadoAutomatico] = useState(false);
  const [puntos, setPuntos] = useState([]);
  const [secuenciales, setSecuenciales] = useState([]);
  const [establecimientos, setEstablecimientos] = useState([]);

  const [docLimit, setDocLimit] = useState(null);
  const [docUsed, setDocUsed] = useState(0);
  const [docAvailable, setDocAvailable] = useState(null);
  const [docUnlimited, setDocUnlimited] = useState(true);

  const [editingLimit, setEditingLimit] = useState(false);
  const [limitInput, setLimitInput] = useState("");

  const isSuper = localStorage.getItem("is_superuser") === "true";

  const refreshEmpresa = async () => {
    try {
      const r = await fetch("/enterprise/empresa/obtener/", {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (r.status === 401 || r.status === 403) {
        setErrorMessage("Sesión expirada");
        return;
      }

      const j = await r.json();

      if (r.ok) {
        setEstablecimientos(j?.empresa?.establecimientos || []);
      }
    } catch {
      setErrorMessage("Error de conexión");
    }
  };

  const refreshEstado = async () => {
    try {
      const r = await fetch("/facturas/firmasec/estado/", {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (r.status === 401 || r.status === 403) {
        setErrorMessage("Sesión expirada");
        return;
      }

      const j = await r.json();
      if (r.ok) {
        setFirmaEstado(j.firma || {});
        setFirmadoAutomatico(!!j.firma?.firmado_automatico);
        setPuntos(j.puntos || []);
        setSecuenciales(j.secuenciales || []);
      } else {
        setErrorMessage(j?.error || "No se pudo cargar el estado");
      }
    } catch {
      setErrorMessage("Error de conexión");
    }
  };

  const refreshDocLimits = async () => {
    try {
      const r = await fetch("/enterprise/empresa/document-limits/", {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (r.status === 401 || r.status === 403) {
        setErrorMessage("Sesión expirada");
        return;
      }

      const j = await r.json();
      if (j.ok) {
        setDocLimit(j.limite);
        setDocUsed(j.usados);
        setDocAvailable(j.disponibles);
        setDocUnlimited(j.ilimitado);
        setLimitInput(j.limite == null ? "" : String(j.limite));
      } else {
        setErrorMessage(j.error || "No se pudo cargar el límite");
      }
    } catch {
      setErrorMessage("Error de conexión");
    }
  };

  const updateLimit = async () => {
    try {
      const csrf = getCSRF();
      const payload = {
        limite: docUnlimited ? "ilimitado" : limitInput,
      };

      const r = await fetch("/enterprise/empresa/update-document-limit/", {
        method: "POST",
        headers: getAuthHeaders({
          "Content-Type": "application/json",
          "X-CSRFToken": csrf,
        }),
        body: JSON.stringify(payload),
      });

      if (r.status === 401 || r.status === 403) {
        const j = await r.json().catch(() => ({}));
        setErrorMessage(j.error || "No autorizado");
        return;
      }

      const j = await r.json();

      if (j.ok) {
        await refreshDocLimits();
        setEditingLimit(false);
        setSuccessMessage("Límite actualizado correctamente");
      } else {
        setErrorMessage(j.error || "No se pudo actualizar");
      }
    } catch {
      setErrorMessage("Error de conexión");
    }
  };

  useEffect(() => {
    refreshEmpresa();
    refreshEstado();
    refreshDocLimits();
  }, []);

  return (
    <Paper
      sx={{
        p: 0,
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
      }}
    >
      <Box
        sx={{
          bgcolor: "#f3f3f3",
          borderBottom: "1px solid #e0e0e0",
          p: 1.5,
        }}
      >
        <Typography sx={{ fontWeight: 800, color: "#333" }}>
          Datos Generales
        </Typography>
      </Box>

      <Table sx={{ "& td": { borderBottom: "1px solid #eaeaea" } }}>
        <TableBody>
          <TableRow>
            <TableCell sx={tdLabelSx}>
              Límite Documentos Electrónicos
              <Box sx={noteSx}>(Límite de documentos emitidos)</Box>
            </TableCell>

            <TableCell sx={tdFieldSx}>
              {!editingLimit && (
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography>{docUnlimited ? "Ilimitado" : docLimit}</Typography>

                  {isSuper && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setEditingLimit(true)}
                      sx={{
                        bgcolor: "#1565C0",
                        color: "#fff",
                        border: "1px solid #1565C0",
                        textTransform: "none",
                        "&:hover": {
                          bgcolor: "#0D47A1",
                          borderColor: "#0D47A1",
                        },
                      }}
                    >
                      Editar
                    </Button>
                  )}
                </Stack>
              )}

              {editingLimit && (
                <Stack spacing={1}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                      size="small"
                      disabled={docUnlimited}
                      value={limitInput}
                      onChange={(e) => setLimitInput(e.target.value.replace(/\D/g, ""))}
                    />

                    <Typography>Ilimitado</Typography>

                    <Switch
                      checked={docUnlimited}
                      onChange={(e) => setDocUnlimited(e.target.checked)}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      onClick={updateLimit}
                      sx={{
                        bgcolor: "#1565C0",
                        color: "#fff",
                        border: "1px solid #1565C0",
                        textTransform: "none",
                        "&:hover": {
                          bgcolor: "#0D47A1",
                          borderColor: "#0D47A1",
                        },
                      }}
                    >
                      Guardar
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => {
                        setEditingLimit(false);
                        refreshDocLimits();
                      }}
                      sx={{
                        textTransform: "none",
                        color: "#1565C0",
                        borderColor: "#1565C0",
                        "&:hover": {
                          borderColor: "#0D47A1",
                          color: "#0D47A1",
                          bgcolor: "rgba(21,101,192,0.04)",
                        },
                      }}
                    >
                      Cancelar
                    </Button>
                  </Stack>
                </Stack>
              )}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>
              Documentos Consumidos
              <Box sx={noteSx}>(Documentos emitidos hasta la fecha)</Box>
            </TableCell>

            <TableCell sx={tdFieldSx}>
              <Typography>{docUsed}</Typography>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>
              Cupo Disponible
              <Box sx={noteSx}>(Cupo disponible para emitir)</Box>
            </TableCell>

            <TableCell sx={tdFieldSx}>
              <Typography>{docUnlimited ? "Ilimitado" : docAvailable}</Typography>
            </TableCell>
          </TableRow>

          <ElectronicSignatureSettings
            firmaEstado={firmaEstado}
            firmadoAutomatico={firmadoAutomatico}
            onToggleAuto={setFirmadoAutomatico}
            onRefresh={refreshEstado}
            getCSRF={getCSRF}
            setSuccessMessage={setSuccessMessage}
            setErrorMessage={setErrorMessage}
          />

          <EmissionAndSequenceSettings
            establecimientos={establecimientos}
            puntos={puntos}
            secuenciales={secuenciales}
            onRefresh={refreshEstado}
            getCSRF={getCSRF}
            setSuccessMessage={setSuccessMessage}
            setErrorMessage={setErrorMessage}
          />
        </TableBody>
      </Table>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3500}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error">{errorMessage}</Alert>
      </Snackbar>
    </Paper>
  );
}